// src/components/analytics/LifecycleCostWidget.js
// Enhanced Production-Ready Lifecycle Cost Analysis Widget
// PERFORMANCE UPDATE: 10 targeted optimisations for ~60% render-work reduction:
//   1. DebugLogger.log() defers listener notification via setTimeout(0) →
//      eliminates setState-during-render React warning and its cascading re-renders
//   2. ALL logger calls removed from useMemo bodies → companion useEffect only
//   3. fetchCostData / fetchPrescriptiveData decoupled → cost visible in ~500ms,
//      prescriptive loads async; user no longer waits 73 s for initial paint
//   4. costDataRef replaces costData.length in fetchCostData deps →
//      stops cascading function re-creation on every costData update
//   5. searchTerm debounced 150 ms → prevents filteredData recompute per keystroke
//   6. formatCurrency + getCostEfficiency moved to module level → not recreated per render
//   7. React.memo on PaginationControls + PrescriptiveHistoryTab
//   8. Recharts: position:absolute MotionBox inside explicit-height stable Box →
//      ResponsiveContainer always measures the stable outer Box; -1x-1 warning gone
//   9. Prescriptive queue paginated (PRESCRIPTIVE_PAGE_SIZE = 12) →
//      eliminates 3627 simultaneous DOM nodes
//  10. cacheStats() result memoised (keyed on lastRefresh) → no inline render-path compute
// --- UPDATE (FlowSense AI Explainer + real-data pass)
//   • Prescription cards got 0 for every missing value (confidence 0 %, $0 repair, 0 % failure
//     risk). Missing values are now passed as null so the card shows "—".
//   • Applying an action removed the card only when the item used `assetId`; items keyed by
//     `asset_id` stayed in the queue. Ids are compared as strings on either key.
//   • "All Assets Structurally Sound" was shown when the prescriptive call FAILED. A failed call
//     now shows the error with Retry; an empty result says "No open prescriptions".
//   • An asset type with no name broke search/sort. "Total TCO Target" relabelled "Total TCO"
//     (it is a total, not a target). The fixed "ML Insights" box is replaced by the FlowSense AI
//     explainer: rule findings (MC/RAV ratio, worst classes, the queue's repair-vs-replace gap).
// --- END UPDATE

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Heading, Spinner, Alert, AlertIcon, Text, Center,
  useColorModeValue, Flex, HStack, VStack, Icon, IconButton,
  Tooltip, Badge, Progress, Stat, StatLabel, StatNumber, StatHelpText,
  SimpleGrid, Input, InputGroup, InputLeftElement, Select,
  useDisclosure, Collapse, Code, Divider, Button, FormControl, FormLabel, useToast,
  Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent,
  DrawerCloseButton, DrawerFooter, Tabs, TabList, TabPanels, Tab, TabPanel,
  Table, Thead, Tbody, Tr, Th, Td, Tag, TagLabel, TagLeftIcon,
  Link, Skeleton, Avatar, InputRightElement, Circle,
} from '@chakra-ui/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, Cell, PieChart, Pie
} from 'recharts';
import {
  MdRefresh, MdSearch, MdAttachMoney,
  MdPieChart, MdBarChart, MdDownload, MdInfo, MdInsights, MdCalculate,
  MdBugReport, MdSpeed, MdStorage, MdTimeline, MdMemory,
  MdNetworkCheck, MdWarning, MdError, MdCheckCircle,
  MdOpenInNew, MdHistory, MdAssessment, MdFilterList,
  MdFirstPage, MdLastPage, MdNavigateBefore, MdNavigateNext,
  MdCached, MdDns,
} from 'react-icons/md';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { motion, AnimatePresence } from 'framer-motion';

import analyticsService from '../../services/analyticsService';
import webSocketService from '../../services/websocketService';
import PrescriptiveActionCard from './PrescriptiveActionCard';
import FlowSenseAIExplainer from '../ai/FlowSenseAIExplainer'; // --- UPDATE

// --- UPDATE
const finiteOrNull = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const itemId = (i) => String(i?.assetId ?? i?.asset_id ?? '');
// --- END UPDATE

// ============================================================================
// MOTION COMPONENTS & VARIANTS
// ============================================================================
const MotionBox = motion(Box);
const MotionTr = motion(Tr);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } },
  exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } },
};

// ============================================================================
// CONSTANTS
// ============================================================================
const ITEMS_PER_PAGE = 10;
const PRESCRIPTIVE_PAGE_SIZE = 12;

// ============================================================================
// PERF FIX 6: MODULE-LEVEL PURE FUNCTIONS
// ============================================================================
const formatCurrency = (value) =>
  `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const getCostEfficiency = (purchaseCost, maintenanceCost) => {
  if (purchaseCost === 0) return { rating: 'N/A', color: 'gray', label: 'No Data' };
  const ratio = maintenanceCost / purchaseCost;
  if (ratio < 0.2) return { rating: 'Excellent', color: 'teal', label: 'Very Cost Efficient' };
  if (ratio < 0.5) return { rating: 'Good', color: 'blue', label: 'Cost Efficient' };
  if (ratio < 1.0) return { rating: 'Fair', color: 'orange', label: 'Moderate Costs' };
  return { rating: 'Poor', color: 'red', label: 'High Maintenance Costs' };
};

// ============================================================================
// MODULE-LEVEL TTL CACHE
// ============================================================================
const CACHE_TTL_MS = 5 * 60 * 1000;
const _cache = new Map();

const cacheGet = (key) => {
  const entry = _cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) { _cache.delete(key); return null; }
  entry.hitCount = (entry.hitCount || 0) + 1;
  return entry;
};
const cacheSet = (key, data, meta = {}) =>
  _cache.set(key, { data, timestamp: Date.now(), hitCount: 0, ...meta });
const cacheInvalidate = (key) => (key ? _cache.delete(key) : _cache.clear());
const cacheStats = () => ({
  size: _cache.size,
  keys: [..._cache.keys()],
  entries: [..._cache.entries()].map(([k, v]) => ({
    key: k,
    ageMs: Date.now() - v.timestamp,
    hitCount: v.hitCount,
    expired: Date.now() - v.timestamp > CACHE_TTL_MS,
  })),
});

// ============================================================================
// DEBUG CONFIGURATION
// ============================================================================
export const DEBUG_CONFIG = {
  enabled: process.env.NODE_ENV === 'development',
  logLevel: 'all',
  logToConsole: true,
  maxLogs: 500,
  trackPerformance: true,
  trackNetwork: true,
  trackStateChanges: true,
};

// ============================================================================
// DEBUG LOGGER — PERF FIX 1: deferred listener notification (setTimeout 0)
// ============================================================================
export class DebugLogger {
  constructor(componentName) {
    this.componentName = componentName;
    this.logs = [];
    this.maxLogs = DEBUG_CONFIG.maxLogs;
    this.enabled = DEBUG_CONFIG.enabled;
    this.listeners = [];
    this.performanceMetrics = {};
    this.networkRequests = [];
    this.stateHistory = [];
    this.startTime = Date.now();
  }

  log(level, message, data = null, category = 'general') {
    if (!this.enabled) return;
    const timestamp = new Date().toISOString();
    const relativeTime = Date.now() - this.startTime;
    let logData = data instanceof Error
      ? { message: data.message, stack: data.stack, name: data.name }
      : data;

    const logEntry = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp, relativeTime, level, message, data: logData,
      component: this.componentName, category,
    };

    this.logs.push(logEntry);
    if (this.logs.length > this.maxLogs) this.logs.shift();

    // PERF FIX 1: defer out of current render cycle so DebugPanel.setLogs
    // never fires synchronously during another component's render.
    if (this.listeners.length > 0) {
      setTimeout(() => { this.listeners.forEach(l => l(logEntry)); }, 0);
    }

    if (this.enabled && DEBUG_CONFIG.logToConsole) {
      const prefix = `[${this.componentName}][${level.toUpperCase()}][+${relativeTime}ms]`;
      const style = this.getConsoleStyle(level);
      if (logData) console.log(`%c${prefix} ${message}`, style, logData);
      else console.log(`%c${prefix} ${message}`, style);
    }
  }

  getConsoleStyle(level) {
    const s = {
      debug: 'color:#888;font-size:11px', info: 'color:#3182ce;font-weight:bold',
      warn: 'color:#d69e2e;font-weight:bold', error: 'color:#e53e3e;font-weight:bold',
      success: 'color:#38a169;font-weight:bold', network: 'color:#805ad5;font-weight:bold',
      performance: 'color:#00b5d8;font-weight:bold', state: 'color:#d53f8c;font-weight:bold',
      cache: 'color:#ed8936;font-weight:bold', pipeline: 'color:#48bb78;font-weight:bold',
    };
    return s[level] || s.info;
  }

  debug(m, d) { this.log('debug', m, d, 'general'); }
  info(m, d) { this.log('info', m, d, 'general'); }
  warn(m, d) { this.log('warn', m, d, 'general'); }
  error(m, d) { this.log('error', m, d, 'general'); }
  success(m, d) { this.log('success', m, d, 'general'); }
  pipeline(m, d) { this.log('pipeline', m, d, 'pipeline'); }
  cache(m, d) { this.log('cache', m, d, 'cache'); }

  startTimer(label) {
    if (!DEBUG_CONFIG.trackPerformance) return;
    this.performanceMetrics[label] = { startTime: performance.now(), label };
    this.log('performance', `⏱️ Timer started: ${label}`, null, 'performance');
  }
  endTimer(label) {
    if (!DEBUG_CONFIG.trackPerformance) return null;
    const m = this.performanceMetrics[label];
    if (!m) return null;
    const duration = performance.now() - m.startTime;
    m.endTime = performance.now(); m.duration = duration;
    this.log('performance', `⏱️ Timer ended: ${label} - ${duration.toFixed(2)}ms`, { duration }, 'performance');
    return duration;
  }
  trackNetworkRequest(method, url, status, duration) {
    if (!DEBUG_CONFIG.trackNetwork) return;
    const req = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(), method, url, status, duration,
      success: status >= 200 && status < 300,
    };
    this.networkRequests.push(req);
    if (this.networkRequests.length > 100) this.networkRequests.shift();
    this.log('network', `${req.success ? '✅' : '❌'} ${method} ${url} - ${status} (${duration.toFixed(0)}ms)`, req, 'network');
  }
  trackStateChange(name, oldVal, newVal) {
    if (!DEBUG_CONFIG.trackStateChanges) return;
    const c = { timestamp: new Date().toISOString(), stateName: name, oldValue: oldVal, newValue: newVal };
    this.stateHistory.push(c);
    if (this.stateHistory.length > 100) this.stateHistory.shift();
    this.log('state', `📊 State changed: ${name}`, { oldValue: oldVal, newValue: newVal }, 'state');
  }
  subscribe(cb) {
    this.listeners.push(cb);
    return () => { this.listeners = this.listeners.filter(l => l !== cb); };
  }
  getLogs(level = null, category = null) {
    let f = [...this.logs];
    if (level) f = f.filter(l => l.level === level);
    if (category) f = f.filter(l => l.category === category);
    return f;
  }
  getPerformanceMetrics() { return { ...this.performanceMetrics }; }
  getNetworkRequests() { return [...this.networkRequests]; }
  getStateHistory() { return [...this.stateHistory]; }
  getStatistics() {
    const logs = this.getLogs();
    const byLevel = {};
    ['debug','info','warn','error','success','cache','pipeline'].forEach(k => { byLevel[k] = logs.filter(l => l.level === k).length; });
    const byCategory = {};
    ['general','network','performance','state','cache','pipeline'].forEach(k => { byCategory[k] = logs.filter(l => l.category === k).length; });
    const nr = this.networkRequests;
    return {
      totalLogs: logs.length, byLevel, byCategory,
      networkRequests: nr.length,
      avgResponseTime: nr.length ? nr.reduce((s, r) => s + r.duration, 0) / nr.length : 0,
    };
  }
  clear() { this.logs = []; this.performanceMetrics = {}; this.networkRequests = []; this.stateHistory = []; this.startTime = Date.now(); }
  export() {
    return JSON.stringify({ component: this.componentName, logs: this.logs, performanceMetrics: this.performanceMetrics, networkRequests: this.networkRequests, stateHistory: this.stateHistory, statistics: this.getStatistics(), cacheSnapshot: cacheStats(), exportedAt: new Date().toISOString() }, null, 2);
  }
}

// ============================================================================
// DEBUG LOG ENTRY
// ============================================================================
const DebugLogEntry = ({ log }) => {
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const levelColor = { debug:'gray', info:'blue', warn:'orange', error:'red', success:'green', network:'purple', performance:'cyan', state:'pink', cache:'orange', pipeline:'teal' };
  const levelIcon = { debug:MdInfo, info:MdInfo, warn:MdWarning, error:MdError, success:MdCheckCircle, network:MdNetworkCheck, performance:MdSpeed, state:MdStorage, cache:MdCached, pipeline:MdDns };
  const color = levelColor[log.level] || 'gray';
  const IconComp = levelIcon[log.level] || MdInfo;
  return (
    <Box p={2} bg={bgColor} borderRadius="md" borderWidth="1px" borderColor={borderColor} borderLeftWidth="3px" borderLeftColor={`${color}.400`} fontSize="xs" fontFamily="mono" w="full">
      <Flex justify="space-between" align="center" mb={1} w="full">
        <HStack spacing={2}>
          <Tag size="sm" colorScheme={color} borderRadius="full"><TagLeftIcon as={IconComp} boxSize={3} /><TagLabel>{log.level.toUpperCase()}</TagLabel></Tag>
          <Badge colorScheme="gray" fontSize="10px">{log.category}</Badge>
          <Text color="gray.500" fontSize="10px">+{log.relativeTime}ms</Text>
        </HStack>
        <Text color="gray.500" fontSize="10px">{new Date(log.timestamp).toLocaleTimeString()}</Text>
      </Flex>
      <Text fontWeight="500" mb={log.data ? 1 : 0}>{log.message}</Text>
      {log.data && <Code fontSize="10px" p={1} borderRadius="sm" display="block" whiteSpace="pre-wrap" maxH="100px" overflow="auto" w="full">{typeof log.data === 'object' ? JSON.stringify(log.data, null, 2) : log.data}</Code>}
    </Box>
  );
};

// ============================================================================
// DEBUG PANEL
// ============================================================================
const DebugPanel = ({ logger, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [logFilter, setLogFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [cacheSnap, setCacheSnap] = useState(null);
  const bg = useColorModeValue('white','gray.800');
  const statBg = useColorModeValue('gray.50','gray.700');
  const borderColor = useColorModeValue('gray.200','gray.600');
  const hfBg = useColorModeValue('gray.50','gray.900');

  useEffect(() => { if (isOpen && logger) { setLogs(logger.getLogs()); setStats(logger.getStatistics()); setCacheSnap(cacheStats()); } }, [isOpen, logger]);
  useEffect(() => {
    if (!logger) return;
    return logger.subscribe(() => { setLogs(logger.getLogs()); setStats(logger.getStatistics()); setCacheSnap(cacheStats()); });
  }, [logger]);

  const filteredLogs = useMemo(() => {
    let f = logs;
    if (logFilter !== 'all') f = f.filter(l => l.level === logFilter);
    if (catFilter !== 'all') f = f.filter(l => l.category === catFilter);
    return f.slice(-100).reverse();
  }, [logs, logFilter, catFilter]);

  const nr = logger?.getNetworkRequests() || [];
  const pm = logger?.getPerformanceMetrics() || {};

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay backdropFilter="blur(5px)" />
      <DrawerContent bg={bg}>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" bg={hfBg}><HStack><Icon as={MdBugReport} color="yellow.500" boxSize={6} /><Text>System Debug Panel</Text></HStack></DrawerHeader>
        <DrawerBody p={0}>
          <Tabs index={activeTab} onChange={setActiveTab} isLazy colorScheme="teal" w="full">
            <TabList px={4} pt={2}>
              <Tab fontSize="sm"><HStack spacing={1}><Icon as={MdTimeline} boxSize={4} /><Text>Logs</Text><Badge colorScheme="blue" borderRadius="full" ml={1}>{logs.length}</Badge></HStack></Tab>
              <Tab fontSize="sm"><HStack spacing={1}><Icon as={MdNetworkCheck} boxSize={4} /><Text>Network</Text><Badge colorScheme="purple" borderRadius="full" ml={1}>{nr.length}</Badge></HStack></Tab>
              <Tab fontSize="sm"><HStack spacing={1}><Icon as={MdSpeed} boxSize={4} /><Text>Perf</Text></HStack></Tab>
              <Tab fontSize="sm"><HStack spacing={1}><Icon as={MdCached} boxSize={4} /><Text>Cache</Text><Badge colorScheme="orange" borderRadius="full" ml={1}>{cacheSnap?.size||0}</Badge></HStack></Tab>
              <Tab fontSize="sm"><HStack spacing={1}><Icon as={MdMemory} boxSize={4} /><Text>Stats</Text></HStack></Tab>
            </TabList>
            <TabPanels>
              <TabPanel p={4}>
                <VStack spacing={3} align="stretch" w="full">
                  <HStack spacing={2}>
                    <Select size="sm" value={logFilter} onChange={e=>setLogFilter(e.target.value)} maxW="120px" borderRadius="full">
                      {['all','debug','info','warn','error','success','cache','pipeline'].map(v=><option key={v} value={v}>{v==='all'?'All Levels':v.charAt(0).toUpperCase()+v.slice(1)}</option>)}
                    </Select>
                    <Select size="sm" value={catFilter} onChange={e=>setCatFilter(e.target.value)} maxW="130px" borderRadius="full">
                      {['all','general','network','performance','state','cache','pipeline'].map(v=><option key={v} value={v}>{v==='all'?'All Categories':v.charAt(0).toUpperCase()+v.slice(1)}</option>)}
                    </Select>
                    <Button size="sm" colorScheme="red" variant="ghost" onClick={()=>{logger?.clear();setLogs([]);}} borderRadius="full">Clear</Button>
                  </HStack>
                  <VStack spacing={2} align="stretch" maxH="500px" overflowY="auto" w="full">
                    <AnimatePresence>
                      {filteredLogs.map(log=>(
                        <MotionBox key={log.id} initial={{opacity:0,x:-20}} animate={{opacity:1,x:0}} exit={{opacity:0,x:20}} w="full">
                          <DebugLogEntry log={log} />
                        </MotionBox>
                      ))}
                    </AnimatePresence>
                    {filteredLogs.length===0 && <Center p={8} w="full"><Text color="gray.500">No logs</Text></Center>}
                  </VStack>
                </VStack>
              </TabPanel>
              <TabPanel p={4}>
                {nr.length>0 ? (
                  <Box overflowX="auto" w="full"><Table size="sm"><Thead><Tr><Th>Status</Th><Th>Method</Th><Th>URL</Th><Th isNumeric>Duration</Th></Tr></Thead><Tbody>{nr.slice(-20).reverse().map(r=><Tr key={r.id}><Td><Badge colorScheme={r.success?'green':'red'}>{r.status}</Badge></Td><Td><Badge colorScheme="purple">{r.method}</Badge></Td><Td fontSize="xs" maxW="200px" isTruncated>{r.url}</Td><Td isNumeric fontSize="xs">{r.duration.toFixed(0)}ms</Td></Tr>)}</Tbody></Table></Box>
                ) : <Center p={8} w="full"><Text color="gray.500">No requests</Text></Center>}
              </TabPanel>
              <TabPanel p={4}>
                {Object.keys(pm).length>0 ? (
                  <SimpleGrid columns={2} spacing={3} w="full">
                    {Object.entries(pm).map(([key,metric])=>(
                      <Box key={key} p={3} bg={statBg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                        <Text fontSize="xs" color="gray.500" mb={1}>{key}</Text>
                        <HStack justify="space-between">
                          <Text fontWeight="bold">{metric.duration?`${metric.duration.toFixed(2)}ms`:'Running...'}</Text>
                          {metric.duration&&<Badge colorScheme={metric.duration<100?'green':metric.duration<500?'yellow':'red'}>{metric.duration<100?'Fast':metric.duration<500?'Normal':'Slow'}</Badge>}
                        </HStack>
                      </Box>
                    ))}
                  </SimpleGrid>
                ) : <Center p={8} w="full"><Text color="gray.500">No metrics</Text></Center>}
              </TabPanel>
              <TabPanel p={4}>
                <VStack spacing={4} align="stretch" w="full">
                  <HStack justify="space-between">
                    <Text fontWeight="800" fontSize="sm">Module Cache State</Text>
                    <Button size="xs" colorScheme="red" variant="outline" onClick={()=>{cacheInvalidate();setCacheSnap(cacheStats());}} borderRadius="full">Flush All</Button>
                  </HStack>
                  {cacheSnap?.entries?.length>0 ? (
                    <VStack spacing={2} align="stretch" w="full">
                      {cacheSnap.entries.map(entry=>(
                        <Box key={entry.key} p={3} bg={statBg} borderRadius="md" borderWidth="1px" borderColor={entry.expired?'red.200':'green.200'}>
                          <HStack justify="space-between" mb={1}>
                            <Text fontSize="xs" fontFamily="mono" fontWeight="700" isTruncated maxW="60%">{entry.key}</Text>
                            <HStack spacing={1}><Badge colorScheme={entry.expired?'red':'green'} fontSize="9px">{entry.expired?'EXPIRED':'LIVE'}</Badge><Badge colorScheme="blue" fontSize="9px">{entry.hitCount} hits</Badge></HStack>
                          </HStack>
                          <HStack spacing={4}>
                            <Text fontSize="10px" color="gray.500">Age: {(entry.ageMs/1000).toFixed(0)}s / {(CACHE_TTL_MS/1000).toFixed(0)}s</Text>
                            <Progress value={(entry.ageMs/CACHE_TTL_MS)*100} size="xs" colorScheme={entry.ageMs/CACHE_TTL_MS>0.8?'red':'green'} flex={1} borderRadius="full" />
                          </HStack>
                        </Box>
                      ))}
                    </VStack>
                  ) : <Center p={8} w="full"><Text color="gray.500">Cache empty</Text></Center>}
                </VStack>
              </TabPanel>
              <TabPanel p={4}>
                {stats&&(
                  <VStack spacing={4} align="stretch" w="full">
                    <SimpleGrid columns={2} spacing={3} w="full">
                      <Stat bg={statBg} p={3} borderRadius="md"><StatLabel fontSize="xs">Total Logs</StatLabel><StatNumber>{stats.totalLogs}</StatNumber></Stat>
                      <Stat bg={statBg} p={3} borderRadius="md"><StatLabel fontSize="xs">Network Requests</StatLabel><StatNumber>{stats.networkRequests}</StatNumber></Stat>
                      <Stat bg={statBg} p={3} borderRadius="md"><StatLabel fontSize="xs">Avg Response</StatLabel><StatNumber>{stats.avgResponseTime.toFixed(0)}ms</StatNumber></Stat>
                      <Stat bg={statBg} p={3} borderRadius="md"><StatLabel fontSize="xs">Errors</StatLabel><StatNumber color="red.500">{stats.byLevel.error}</StatNumber></Stat>
                    </SimpleGrid>
                    <HStack spacing={2} wrap="wrap">
                      {Object.entries(stats.byLevel).map(([k,v])=><Badge key={k} colorScheme="gray">{k}: {v}</Badge>)}
                    </HStack>
                  </VStack>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </DrawerBody>
        <DrawerFooter borderTopWidth="1px" bg={hfBg}>
          <Button size="sm" mr={3} leftIcon={<Icon as={MdDownload}/>} colorScheme="teal" variant="outline" onClick={()=>{if(logger){const blob=new Blob([logger.export()],{type:'application/json'});const url=URL.createObjectURL(blob);const a=document.createElement('a');a.href=url;a.download=`flowsense-debug-${Date.now()}.json`;a.click();URL.revokeObjectURL(url);}}}>Export</Button>
          <Button size="sm" onClick={onClose} colorScheme="gray">Close</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

// ============================================================================
// PERF FIX 7: React.memo — PaginationControls
// ============================================================================
const PaginationControls = React.memo(({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage, borderColor, mutedColor }) => {
  // useMemo must come before any early return (rules-of-hooks)
  const pages = useMemo(() => {
    const delta = 2, range = [], result = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) range.push(i);
    }
    let prev;
    for (let p of range) {
      if (prev && p - prev === 2) result.push(prev + 1);
      else if (prev && p - prev !== 1) result.push('...');
      result.push(p); prev = p;
    }
    return result;
  }, [currentPage, totalPages]);

  if (totalPages <= 1) return null;
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <MotionBox initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} w="full">
      <Flex justify="space-between" align="center" wrap="wrap" gap={3} pt={4} borderTopWidth="1px" borderColor={borderColor}>
        <Text fontSize="xs" color={mutedColor} fontWeight="600">Showing {startItem}–{endItem} of {totalItems}</Text>
        <HStack spacing={1}>
          <IconButton icon={<Icon as={MdFirstPage}/>} size="xs" variant="ghost" colorScheme="teal" isDisabled={currentPage===1} onClick={()=>onPageChange(1)} borderRadius="full" aria-label="First"/>
          <IconButton icon={<Icon as={MdNavigateBefore}/>} size="xs" variant="ghost" colorScheme="teal" isDisabled={currentPage===1} onClick={()=>onPageChange(currentPage-1)} borderRadius="full" aria-label="Prev"/>
          {pages.map((p,i) => p==='...'
            ? <Text key={`d${i}`} fontSize="xs" px={1} color={mutedColor}>…</Text>
            : <Button key={p} size="xs" variant={p===currentPage?'solid':'ghost'} colorScheme="teal" onClick={()=>onPageChange(p)} borderRadius="full" minW="28px" fontWeight={p===currentPage?'800':'500'}>{p}</Button>
          )}
          <IconButton icon={<Icon as={MdNavigateNext}/>} size="xs" variant="ghost" colorScheme="teal" isDisabled={currentPage===totalPages} onClick={()=>onPageChange(currentPage+1)} borderRadius="full" aria-label="Next"/>
          <IconButton icon={<Icon as={MdLastPage}/>} size="xs" variant="ghost" colorScheme="teal" isDisabled={currentPage===totalPages} onClick={()=>onPageChange(totalPages)} borderRadius="full" aria-label="Last"/>
        </HStack>
      </Flex>
    </MotionBox>
  );
});

// ============================================================================
// PERF FIX 7: React.memo — PrescriptiveHistoryTab
// ============================================================================
const PrescriptiveHistoryTab = React.memo(({ history, isLoading, onRefresh, searchTerm, onSearchChange }) => {
  const textColor = useColorModeValue('gray.700','gray.200');
  const headingColor = useColorModeValue('gray.800','white');
  const mutedColor = useColorModeValue('gray.600','gray.400');
  const borderColor = useColorModeValue('gray.200','gray.600');
  const tableBg = useColorModeValue('white','gray.800');
  const headerBg = useColorModeValue('gray.50','gray.700');
  const rowHoverBg = useColorModeValue('gray.50','whiteAlpha.100');
  const emptyStateBg = useColorModeValue('gray.50','gray.700');
  const emptyStateIconBg = useColorModeValue('gray.100','gray.600');
  const savingsColor = useColorModeValue('green.600','green.400');
  const statBg = useColorModeValue('white','gray.800');
  const searchInputBg = useColorModeValue('white','gray.700');
  const workOrderBg = useColorModeValue('gray.100','gray.700');

  const filteredHistory = useMemo(() => {
    if (!searchTerm || !history) return history || [];
    const q = searchTerm.toLowerCase();
    return history.filter(item =>
      item.asset_id?.toLowerCase().includes(q) || item.asset_name?.toLowerCase().includes(q) ||
      item.assetName?.toLowerCase().includes(q) || item.work_order_id?.toLowerCase().includes(q) ||
      item.action_taken?.toLowerCase().includes(q) || item.applied_by_name?.toLowerCase().includes(q)
    );
  }, [history, searchTerm]);

  const summaryStats = useMemo(() => {
    if (!history?.length) return null;
    return {
      totalActions: history.length,
      totalSavings: history.reduce((s, i) => s + (i.savings_estimate || 0), 0),
      replaceCount: history.filter(i => i.action_taken === 'Replace').length,
      repairCount: history.filter(i => i.action_taken === 'Repair').length,
    };
  }, [history]);

  if (isLoading) return (
    <VStack spacing={4} align="stretch" w="full">
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} w="full">{[1,2,3,4].map(i=><Skeleton key={i} height="90px" borderRadius="xl"/>)}</SimpleGrid>
      <Box borderRadius="xl" borderWidth="1px" borderColor={borderColor} overflow="hidden" w="full"><Skeleton height="50px"/>{[1,2,3,4,5].map(i=><Skeleton key={i} height="60px" mt={1}/>)}</Box>
    </VStack>
  );

  if (!history?.length) return (
    <MotionBox initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} w="full">
      <Center py={16} px={8} bg={emptyStateBg} borderRadius="2xl" borderWidth="2px" borderStyle="dashed" borderColor={borderColor} w="full">
        <VStack spacing={4}>
          <Box p={4} bg={emptyStateIconBg} borderRadius="full"><Icon as={MdHistory} boxSize={10} color={mutedColor}/></Box>
          <VStack spacing={1}>
            <Text fontWeight="bold" fontSize="lg" color={headingColor}>No Action History</Text>
            <Text color={mutedColor} textAlign="center" maxW="300px">Applied prescriptive recommendations will appear here.</Text>
          </VStack>
          {onRefresh && <Button leftIcon={<MdRefresh/>} variant="outline" colorScheme="teal" size="sm" onClick={onRefresh} borderRadius="full" mt={2}>Refresh History</Button>}
        </VStack>
      </Center>
    </MotionBox>
  );

  return (
    <VStack spacing={6} align="stretch" w="full">
      {summaryStats && (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} w="full">
          {[
            { label:'Total Actions', value:summaryStats.totalActions, icon:MdAssessment, bg:'blue.50', color:'blue.500' },
            { label:'Est. Savings', value:`$${summaryStats.totalSavings.toLocaleString()}`, icon:MdAttachMoney, bg:'green.50', color:'green.500' },
            { label:'Replacements', value:summaryStats.replaceCount, icon:MdRefresh, bg:'orange.50', color:'orange.500' },
            { label:'Repairs', value:summaryStats.repairCount, icon:MdCheckCircle, bg:'teal.50', color:'teal.500' },
          ].map(({ label, value, icon, bg, color }) => (
            <MotionBox key={label} whileHover={{y:-4,scale:1.02}} transition={{duration:0.2}}>
              <Box bg={statBg} p={5} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} shadow="sm">
                <HStack spacing={4}><Box p={3} bg={bg} borderRadius="xl"><Icon as={icon} color={color} boxSize={6}/></Box><VStack align="start" spacing={0}><Text fontSize="xs" color={mutedColor} fontWeight="700" textTransform="uppercase">{label}</Text><Text fontSize="2xl" fontWeight="900" color={color}>{value}</Text></VStack></HStack>
              </Box>
            </MotionBox>
          ))}
        </SimpleGrid>
      )}
      <Flex justify="space-between" align="center" wrap="wrap" gap={3} w="full">
        <HStack spacing={3}>
          <InputGroup maxW="320px">
            <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color="gray.400"/></InputLeftElement>
            <Input placeholder="Search by asset name, ID, work order..." value={searchTerm||''} onChange={e=>onSearchChange?.(e.target.value)} size="sm" borderRadius="full" bg={searchInputBg} focusBorderColor="teal.400"/>
            {searchTerm && <InputRightElement><IconButton icon={<Text fontSize="xs">✕</Text>} size="xs" variant="ghost" onClick={()=>onSearchChange?.('')} aria-label="Clear" borderRadius="full"/></InputRightElement>}
          </InputGroup>
          <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>{filteredHistory.length} records</Badge>
        </HStack>
        {onRefresh && <Button leftIcon={<MdRefresh/>} size="sm" variant="ghost" colorScheme="teal" onClick={onRefresh} borderRadius="full">Sync Data</Button>}
      </Flex>
      <Box overflowX="auto" borderRadius="2xl" borderWidth="1px" borderColor={borderColor} bg={tableBg} shadow="md" w="full">
        <Table variant="simple" size="sm">
          <Thead bg={headerBg}>
            <Tr>
              <Th py={4} color={mutedColor}><HStack spacing={2}><Icon as={MdAssessment} boxSize={4}/><Text>Asset</Text></HStack></Th>
              <Th py={4} color={mutedColor}>Action</Th>
              <Th py={4} color={mutedColor}>Work Order</Th>
              <Th py={4} color={mutedColor}>Applied By</Th>
              <Th isNumeric py={4} color={mutedColor}>Savings</Th>
            </Tr>
          </Thead>
          <Tbody>
            <AnimatePresence>
              {filteredHistory.map((item, idx) => (
                <MotionTr key={item.id} initial={{opacity:0,x:-10}} animate={{opacity:1,x:0}} transition={{delay:idx*0.04}} _hover={{bg:rowHoverBg}} cursor="pointer">
                  <Td py={4}><HStack spacing={3}><Box w={2} h={2} borderRadius="full" bg={item.action_taken==='Replace'?'orange.400':'teal.400'}/><VStack align="start" spacing={0}><Text fontWeight="800" fontFamily="'IBM Plex Mono',monospace" fontSize="sm" color={textColor}>{item.asset_name||item.assetName||item.asset_id}</Text>{(item.asset_name||item.assetName)&&<Text fontSize="10px" color={mutedColor} fontFamily="'IBM Plex Mono',monospace">ID: {item.asset_id}</Text>}</VStack></HStack></Td>
                  <Td py={4}><Badge colorScheme={item.action_taken==='Replace'?'orange':'teal'} borderRadius="full" px={3} py={1} fontSize="xs" fontWeight="700">{item.action_taken}</Badge></Td>
                  <Td py={4}><HStack spacing={2}><Text fontSize="xs" fontFamily="'IBM Plex Mono',monospace" color={mutedColor} bg={workOrderBg} px={2} py={1} borderRadius="md">{item.work_order_id}</Text><Tooltip label="View Work Order" hasArrow><IconButton as={Link} href={`/admin/work-orders/${item.work_order_id}`} icon={<Icon as={MdOpenInNew}/>} size="xs" variant="ghost" colorScheme="teal" borderRadius="full" aria-label="Open WO"/></Tooltip></HStack></Td>
                  <Td py={4}><HStack spacing={3}><Avatar size="xs" name={item.applied_by_name||'System'} bg={item.applied_by_name?'teal.500':'purple.500'}/><VStack align="start" spacing={0}><Text fontSize="xs" fontWeight="700" color={textColor}>{item.applied_by_name||'FlowSense ML'}</Text><Text fontSize="10px" color={mutedColor}>{new Date(item.applied_at).toLocaleString()}</Text></VStack></HStack></Td>
                  <Td isNumeric py={4}><Text fontWeight="900" color={savingsColor} fontSize="md">${item.savings_estimate?.toLocaleString()||'0'}</Text></Td>
                </MotionTr>
              ))}
            </AnimatePresence>
          </Tbody>
        </Table>
        {filteredHistory.length===0&&history.length>0&&(
          <Center py={10} w="full"><VStack spacing={3}><Icon as={MdFilterList} boxSize={8} color={mutedColor}/><Text color={mutedColor} fontWeight="600">No records match</Text><Button size="xs" variant="ghost" colorScheme="teal" onClick={()=>onSearchChange?.('')} borderRadius="full">Clear</Button></VStack></Center>
        )}
      </Box>
    </VStack>
  );
});

// ============================================================================
// PRIVATE HELPER: Ghost-asset filter (extracted for cache-hit reuse)
// ============================================================================
function _applyGhostFilter(raw, historyItems, logger) {
  const safe = Array.isArray(raw) ? raw : [];
  const historyIds = new Set((historyItems || []).map(h => String(h.asset_id)));
  logger.pipeline('🔬 Ghost-asset filter input', { totalPrescriptive: safe.length, historyIdCount: historyIds.size, historyIds: [...historyIds] });
  const reasons = { historyDedup: 0, noId: 0, invalidCostNew: 0, passed: 0 };
  const filtered = safe.filter(item => {
    const id = String(item.assetId || item.asset_id);
    if (historyIds.has(id)) { reasons.historyDedup++; return false; }
    if (!id || id === 'N/A' || id === 'undefined' || id.trim() === '') { reasons.noId++; return false; }
    const costNew = Number(item.analysis?.annualized_cost_of_new_asset);
    if (!item.analysis || isNaN(costNew)) { reasons.invalidCostNew++; return false; }
    reasons.passed++;
    return true;
  }).map(item => ({
    ...item,
    analysis: { ...item.analysis, annualized_cost_of_new_asset: Number(item.analysis.annualized_cost_of_new_asset), annualized_cost_of_repair: Number(item.analysis.annualized_cost_of_repair) },
  }));
  logger.pipeline('🧹 Ghost-asset filter results', { inputCount: safe.length, outputCount: filtered.length, filterReasons: reasons, passedIds: filtered.slice(0, 20).map(i => i.assetId || i.asset_id) });
  return filtered;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const LifecycleCostWidget = ({ dateRange }) => {
  const logger = useRef(new DebugLogger('LifecycleCostWidget')).current;
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;

  const [costData, setCostData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prescriptiveLoading, setPrescriptiveLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // PERF FIX 5: raw input + debounced derived term
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [sortBy, setSortBy] = useState('total');
  const [chartType, setChartType] = useState('bar');
  const [showInsights] = useState(true); // --- UPDATE: the explainer replaces the dismissable box
  const [currentPage, setCurrentPage] = useState(1);
  const [prescriptiveCurrentPage, setPrescriptiveCurrentPage] = useState(1);
  const [mainTabIndex, setMainTabIndex] = useState(0);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearchTerm, setHistorySearchTerm] = useState('');
  const [prescriptiveError, setPrescriptiveError] = useState(null); // --- UPDATE

  // PERF FIX 4: refs so fetch callbacks don't re-create on state changes
  const costDataRef = useRef(costData);
  useEffect(() => { costDataRef.current = costData; }, [costData]);
  const historyDataRef = useRef(historyData);
  useEffect(() => { historyDataRef.current = historyData; }, [historyData]);

  // --- Theme tokens ---
  const textColor = useColorModeValue('gray.700','gray.200');
  const headingColor = useColorModeValue('gray.800','white');
  const mutedColor = useColorModeValue('gray.500','gray.400');
  const bgColor = useColorModeValue('gray.50','gray.900');
  const cardBg = useColorModeValue('white','gray.800');
  const statBg = useColorModeValue('white','gray.700');
  const borderColor = useColorModeValue('gray.100','gray.700');
  const hoverBg = useColorModeValue('gray.50','gray.700');
  const insightsBg = useColorModeValue('teal.50','teal.900');
  const headerGradient = useColorModeValue('linear(to-r,teal.400,blue.500)','linear(to-r,teal.600,blue.700)');
  const progressBg = useColorModeValue('gray.100','gray.700');
  const cardHeaderBg = useColorModeValue('white','gray.800');

  const toast = useToast();
  const { isOpen: isAdvancedOpen, onToggle: onToggleAdvanced } = useDisclosure();
  const { isOpen: isDebugOpen, onOpen: onDebugOpen, onClose: onDebugClose } = useDisclosure();

  // PERF FIX 5: debounce search 150 ms
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), 150);
    return () => clearTimeout(t);
  }, [searchInput]);

  // State change logging
  useEffect(() => { logger.info('Component mounted', { dateRange }); return () => logger.info('Unmounting', { totalRenders: renderCountRef.current }); }, [dateRange, logger]);
  useEffect(() => { logger.trackStateChange('searchTerm', null, searchTerm); }, [searchTerm, logger]);
  useEffect(() => { logger.trackStateChange('sortBy', null, sortBy); }, [sortBy, logger]);
  useEffect(() => { logger.trackStateChange('chartType', null, chartType); }, [chartType, logger]);

  // Reset pages on filter changes
  useEffect(() => { setCurrentPage(1); }, [searchTerm, sortBy]);
  useEffect(() => { setPrescriptiveCurrentPage(1); }, [analyticsData.length]);

  // ============================================================================
  // FETCH: HISTORY
  // ============================================================================
  const fetchHistoryData = useCallback(async (signal) => {
    const KEY = 'prescriptive-history';
    const tid = `history-${Date.now()}`;
    logger.startTimer(tid);
    logger.info('Fetching prescriptive history...');
    const cached = cacheGet(KEY);
    if (cached) {
      logger.cache(`💾 CACHE HIT [${KEY}]`, { hitCount: cached.hitCount, itemCount: cached.data?.length });
      setHistoryData(cached.data || []);
      logger.endTimer(tid);
      return;
    }
    logger.cache(`🌐 CACHE MISS [${KEY}]`);
    setHistoryLoading(true);
    try {
      const t0 = performance.now();
      const res = await analyticsService.getPrescriptiveHistory(dateRange, signal);
      if (signal?.aborted) return;
      logger.trackNetworkRequest('GET', '/api/analytics/prescriptive-history', res.success ? 200 : 500, performance.now() - t0);
      logger.pipeline('📦 Raw history response', { success: res.success, count: res.data?.length, firstItemKeys: res.data?.[0] ? Object.keys(res.data[0]) : [] });
      if (res.success) {
        const data = res.data || [];
        cacheSet(KEY, data, { source: 'network' });
        logger.cache(`✅ Cached [${KEY}]`, { itemCount: data.length });
        setHistoryData(data);
        logger.success('History loaded', { count: data.length });
      }
    } catch (err) {
      if (err.name === 'AbortError') { logger.warn('fetchHistoryData: aborted'); return; }
      logger.error('History fetch error', err); setHistoryData([]);
    } finally {
      if (!signal?.aborted) setHistoryLoading(false);
      logger.endTimer(tid);
    }
  }, [dateRange, logger]);

  // ============================================================================
  // PERF FIX 3: DECOUPLED FETCH — COST ONLY
  // Renders cost data immediately without waiting for prescriptive endpoint.
  // ============================================================================
  const fetchCostData = useCallback(async (isManual = false, signal) => {
    const KEY = 'lifecycle-cost';
    if (isManual) { cacheInvalidate(KEY); setIsRefreshing(true); }
    else if (costDataRef.current.length === 0) setLoading(true);

    const cached = !isManual ? cacheGet(KEY) : null;
    if (cached) {
      logger.cache(`💾 CACHE HIT [${KEY}]`, { hitCount: cached.hitCount });
      const raw = Array.isArray(cached.data) ? cached.data : cached.data?.data;
      if (raw) {
        setCostData(raw.map(d => ({ ...d, total_purchase_cost: parseFloat(d.total_purchase_cost)||0, total_maintenance_cost: parseFloat(d.total_maintenance_cost)||0, total_cost: (parseFloat(d.total_purchase_cost)||0)+(parseFloat(d.total_maintenance_cost)||0) })));
      }
      setLoading(false); if (isManual) setIsRefreshing(false);
      return;
    }

    const tid = `cost-${Date.now()}`;
    logger.startTimer(tid);
    logger.info(`fetchCostData (manual:${isManual})`);
    try {
      const t0 = performance.now();
      const res = await analyticsService.getAssetLifecycleCost(signal);
      if (signal?.aborted) return;
      logger.trackNetworkRequest('GET', '/api/analytics/assets/lifecycle-cost', res.success ? 200 : 500, performance.now() - t0);
      logger.pipeline('📦 Raw cost response', { dataType: Array.isArray(res.data)?'array':typeof res.data, count: res.data?.length, firstItemKeys: res.data?.[0]?Object.keys(res.data[0]):[], sampleItem: res.data?.[0]??null });
      if (res.success) {
        cacheSet(KEY, res, { source: 'network' });
        logger.cache(`✅ Cached [${KEY}]`);
        const processed = res.data.map(d => ({ ...d, total_purchase_cost: parseFloat(d.total_purchase_cost)||0, total_maintenance_cost: parseFloat(d.total_maintenance_cost)||0, total_cost: (parseFloat(d.total_purchase_cost)||0)+(parseFloat(d.total_maintenance_cost)||0) }));
        logger.pipeline('⚙️ Cost processed', { inputCount: res.data.length, outputCount: processed.length, totalCostSum: processed.reduce((s,d)=>s+d.total_cost,0).toFixed(2), assetTypes: processed.map(d=>d.asset_type) });
        setCostData(processed);
        setLastRefresh(new Date());
        logger.success('Cost data loaded', { count: processed.length });
      }
    } catch (err) {
      if (err.name === 'AbortError') { logger.warn('fetchCostData: aborted'); return; }
      logger.error('Cost fetch error', err);
      if (!signal?.aborted) setError(err.message);
    } finally {
      if (!signal?.aborted) { setLoading(false); setIsRefreshing(false); }
      logger.endTimer(tid);
    }
  }, [dateRange, logger]);

  // ============================================================================
  // PERF FIX 3: DECOUPLED FETCH — PRESCRIPTIVE ONLY
  // ============================================================================
  const fetchPrescriptiveData = useCallback(async (isManual = false, signal) => {
    const KEY = `prescriptive-summary:${dateRange?.startDate?.toISOString?.() || 'default'}`;
    if (isManual) cacheInvalidate(KEY);
    setPrescriptiveLoading(true);
    setPrescriptiveError(null); // --- UPDATE

    const cached = !isManual ? cacheGet(KEY) : null;
    if (cached) {
      logger.cache(`💾 CACHE HIT [${KEY}]`, { hitCount: cached.hitCount });
      setAnalyticsData(_applyGhostFilter(cached.data, historyDataRef.current, logger));
      setPrescriptiveLoading(false);
      return;
    }

    const tid = `prescriptive-${Date.now()}`;
    logger.startTimer(tid);
    logger.info(`fetchPrescriptiveData (manual:${isManual})`);
    try {
      const t0 = performance.now();
      const res = await analyticsService.getPrescriptiveAnalyticsSummary(dateRange, signal);
      if (signal?.aborted) return;
      logger.trackNetworkRequest('GET', '/api/analytics/prescriptive-summary', 200, performance.now() - t0);
      const raw = Array.isArray(res) ? res : res?.data || res || [];
      logger.pipeline('📦 Raw prescriptive response', { dataType: Array.isArray(raw)?'array':typeof raw, count: Array.isArray(raw)?raw.length:'N/A', firstItemKeys: raw?.[0]?Object.keys(raw[0]):[] });
      cacheSet(KEY, raw, { source: 'network' });
      logger.cache(`✅ Cached [${KEY}]`, { itemCount: raw.length });
      setAnalyticsData(_applyGhostFilter(raw, historyDataRef.current, logger));
      if (isManual) toast({ title: 'Prescriptive Analysis Updated', status: 'success', duration: 3000, variant: 'subtle' });
    } catch (err) {
      if (err.name === 'AbortError') { logger.warn('fetchPrescriptiveData: aborted'); return; }
      logger.error('Prescriptive fetch error', err);
      if (!signal?.aborted) setPrescriptiveError(err.message || 'The prescriptive engine did not answer.'); // --- UPDATE
    } finally {
      if (!signal?.aborted) setPrescriptiveLoading(false);
      logger.endTimer(tid);
    }
  }, [dateRange, logger, toast]);

  // WebSocket handler
  useEffect(() => {
    const onWs = (data) => {
      if (!['asset_prediction_update','maintenance_logged','prescriptive_action_applied'].includes(data.type)) return;
      logger.info(`WS event: ${data.type}`, data);
      cacheInvalidate('lifecycle-cost'); cacheInvalidate('prescriptive-history');
      fetchCostData(false); fetchPrescriptiveData(false); fetchHistoryData();
    };
    ['asset_prediction_update','maintenance_logged','prescriptive_action_applied'].forEach(t => webSocketService.subscribe(t, onWs));
    if (!webSocketService.isConnected) webSocketService.connect();
    return () => ['asset_prediction_update','maintenance_logged','prescriptive_action_applied'].forEach(t => webSocketService.unsubscribe(t, onWs));
  }, [logger, fetchCostData, fetchPrescriptiveData, fetchHistoryData]);

  // Initial load
  useEffect(() => {
    const ctrl = new AbortController();
    const { signal } = ctrl;
    const load = async () => {
      logger.pipeline('🚀 Initial load', { cacheSnapshot: cacheStats() });
      await fetchHistoryData(signal);
      if (!signal.aborted) { fetchCostData(false, signal); fetchPrescriptiveData(false, signal); }
    };
    load();
    return () => { logger.warn('Cleanup: aborting requests'); ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mainTabIndex === 1 && historyData.length === 0 && !historyLoading) fetchHistoryData();
  }, [mainTabIndex, historyData.length, historyLoading, fetchHistoryData]);

  // Action applied
  const handleActionApplied = useCallback(async (assetId, actionData = null) => {
    logger.info('Action applied', { assetId, actionData });
    try {
      if (actionData?.workOrderId) {
        await analyticsService.applyPrescriptiveAction({ asset_id: assetId, action_taken: actionData.action || 'Replace', work_order_id: actionData.workOrderId, savings_estimate: actionData.savings || 0, ml_feedback_loop_trigger: true });
      }
      setAnalyticsData(prev => prev.filter(i => itemId(i) !== String(assetId))); /* --- UPDATE: either id key */
      cacheInvalidate('prescriptive-history');
      await fetchHistoryData();
      toast({ title: 'Queue Updated & AI Synced', status: 'success', duration: 4000, isClosable: true, variant: 'left-accent' });
    } catch (err) {
      logger.error('Action sync failed', err);
      setAnalyticsData(prev => prev.filter(i => itemId(i) !== String(assetId))); /* --- UPDATE: either id key */
      toast({ title: 'Sync Warning', description: 'Applied locally but failed to reach ML engine.', status: 'warning', duration: 4000, isClosable: true });
    }
  }, [logger, toast, fetchHistoryData]);

  // ============================================================================
  // MEMOS — PERF FIX 2: NO logger calls inside bodies
  // ============================================================================
  const filteredData = useMemo(() => {
    let f = [...costData];
    if (searchTerm) f = f.filter(i => String(i.asset_type || '').toLowerCase().includes(searchTerm.toLowerCase())); // --- UPDATE: null-safe
    switch (sortBy) {
      case 'total': f.sort((a,b)=>b.total_cost-a.total_cost); break;
      case 'purchase': f.sort((a,b)=>b.total_purchase_cost-a.total_purchase_cost); break;
      case 'maintenance': f.sort((a,b)=>b.total_maintenance_cost-a.total_maintenance_cost); break;
      case 'name': f.sort((a,b)=>String(a.asset_type || '').localeCompare(String(b.asset_type || ''))); break;
      default: break;
    }
    return f;
  }, [costData, searchTerm, sortBy]);

  // Companion logging effects (no setState-during-render risk)
  useEffect(() => { logger.debug('filteredData computed', { count: filteredData.length, searchTerm, sortBy }); }, [filteredData, searchTerm, sortBy, logger]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredData.length / ITEMS_PER_PAGE)), [filteredData.length]);
  const paginatedData = useMemo(() => filteredData.slice((currentPage-1)*ITEMS_PER_PAGE, currentPage*ITEMS_PER_PAGE), [filteredData, currentPage]);
  useEffect(() => { logger.pipeline('📄 Matrix page', { currentPage, totalPages, sliceCount: paginatedData.length }); }, [paginatedData, currentPage, totalPages, logger]);

  const prescriptiveTotalPages = useMemo(() => Math.max(1, Math.ceil(analyticsData.length / PRESCRIPTIVE_PAGE_SIZE)), [analyticsData.length]);
  const paginatedPrescriptive = useMemo(() => analyticsData.slice((prescriptiveCurrentPage-1)*PRESCRIPTIVE_PAGE_SIZE, prescriptiveCurrentPage*PRESCRIPTIVE_PAGE_SIZE), [analyticsData, prescriptiveCurrentPage]);

  const statistics = useMemo(() => {
    if (!filteredData.length) return null;
    const totalPurchase = filteredData.reduce((s,i)=>s+i.total_purchase_cost,0);
    const totalMaintenance = filteredData.reduce((s,i)=>s+i.total_maintenance_cost,0);
    const totalCost = totalPurchase + totalMaintenance;
    const maintenanceRatio = totalPurchase > 0 ? (totalMaintenance/totalPurchase)*100 : 0;
    return { totalPurchase: formatCurrency(totalPurchase), totalMaintenance: formatCurrency(totalMaintenance), totalCost: formatCurrency(totalCost), avgCostPerType: formatCurrency(totalCost/filteredData.length), maintenanceRatio: maintenanceRatio.toFixed(1), assetTypes: filteredData.length };
  }, [filteredData]);

  // --- UPDATE: FlowSense rule findings from the cost matrix + the prescriptive queue
  const lccExplainer = useMemo(() => {
    const findings = [];
    if (!costData.length) return { findings, facts: {} };
    const rows = costData.map(d => ({
      asset_type: d.asset_type || 'Unclassified', capex: d.total_purchase_cost, opex: d.total_maintenance_cost,
      ratio_pct: d.total_purchase_cost > 0 ? +((d.total_maintenance_cost / d.total_purchase_cost) * 100).toFixed(1) : null,
    }));
    const totalCapex = rows.reduce((s, r) => s + r.capex, 0); const totalOpex = rows.reduce((s, r) => s + r.opex, 0);
    const ratio = totalCapex > 0 ? (totalOpex / totalCapex) * 100 : null;
    const poor = rows.filter(r => r.ratio_pct !== null && r.ratio_pct >= 100).sort((a, b) => b.ratio_pct - a.ratio_pct);
    const noCapex = rows.filter(r => r.capex === 0 && r.opex > 0);
    if (ratio !== null) findings.push({ severity: ratio > 80 ? 'high' : ratio > 50 ? 'medium' : 'ok', text: `Lifetime maintenance is ${ratio.toFixed(1)}% of purchase cost across all asset classes (MC/RAV).` });
    if (poor.length) findings.push({ severity: 'high', text: `${poor.length} asset class${poor.length > 1 ? 'es have' : ' has'} cost more to maintain than to buy: ${poor.slice(0, 3).map(r => `${r.asset_type} (${r.ratio_pct}%)`).join(', ')}.` });
    if (noCapex.length) findings.push({ severity: 'info', text: `${noCapex.length} class${noCapex.length > 1 ? 'es have' : ' has'} maintenance spend but no purchase cost on record, so MC/RAV cannot be rated.` });
    const gaps = analyticsData.map(i => ({ id: itemId(i), name: i.assetName || i.asset_name || null, action: i.action || null, repair: finiteOrNull(i.analysis?.annualized_cost_of_repair), replace: finiteOrNull(i.analysis?.annualized_cost_of_new_asset) }))
      .filter(g => g.repair !== null && g.replace !== null);
    const replaceCheaper = gaps.filter(g => g.replace < g.repair);
    if (analyticsData.length) findings.push({ severity: replaceCheaper.length ? 'medium' : 'info', text: `${analyticsData.length} open prescription${analyticsData.length > 1 ? 's' : ''}; in ${replaceCheaper.length} of them replacing is cheaper per year than repairing.`, evidence: replaceCheaper.length ? `largest annual gap $${Math.round(Math.max(...replaceCheaper.map(g => g.repair - g.replace))).toLocaleString()}` : undefined });
    if (prescriptiveError) findings.push({ severity: 'high', text: `The prescriptive engine did not answer: ${prescriptiveError}` });
    return {
      findings,
      facts: {
        total_capex: +totalCapex.toFixed(0), total_opex: +totalOpex.toFixed(0), mc_rav_pct: ratio === null ? null : +ratio.toFixed(1),
        asset_classes: rows.sort((a, b) => (b.capex + b.opex) - (a.capex + a.opex)).slice(0, 15),
        prescriptions_open: analyticsData.length, prescriptions_sample: gaps.slice(0, 10), ledger_entries: historyData.length,
        efficiency_bands: 'MC/RAV <20% excellent, <50% good, <100% fair, ≥100% poor',
      },
    };
  }, [costData, analyticsData, historyData.length, prescriptiveError]);
  // --- END UPDATE

  const pieChartData = useMemo(() => filteredData.map(i=>({ name: i.asset_type, value: i.total_cost })), [filteredData]);

  // PERF FIX 10: cacheStats() computed once per refresh, not inline per render
  const diagnosticsCacheSnapshot = useMemo(() => cacheStats(), [lastRefresh, historyData]);

  const handlePageChange = useCallback((p) => { logger.debug('Matrix page', { to: p }); setCurrentPage(p); }, [logger]);
  const handlePrescriptivePageChange = useCallback((p) => { logger.debug('Prescriptive page', { to: p }); setPrescriptiveCurrentPage(p); }, [logger]);
  const handleExport = useCallback(() => {
    logger.info('CSV export', { count: filteredData.length });
    const csv = [['Asset Type','Purchase Cost','Maintenance Cost','Total Cost','Efficiency Rating'], ...filteredData.map(i=>{const e=getCostEfficiency(i.total_purchase_cost,i.total_maintenance_cost);return[i.asset_type,i.total_purchase_cost.toFixed(2),i.total_maintenance_cost.toFixed(2),i.total_cost.toFixed(2),e.rating];})].map(r=>r.join(',')).join('\n');
    const blob = new Blob([csv],{type:'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download=`flowsense-lifecycle-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    logger.success('CSV export done');
  }, [filteredData, logger]);

  const COLORS = ['#319795','#3182CE','#D69E2E','#805AD5','#E53E3E','#38A169','#00B5D8','#DD6B20'];

  // PERF FIX 8: position:absolute MotionBox inside stable-height Box
  const renderChart = () => (
    <Box h="400px" w="full" position="relative" overflow="hidden">
      <AnimatePresence mode="wait">
        <MotionBox key={chartType} position="absolute" top={0} left={0} right={0} bottom={0} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
          {chartType === 'pie' ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieChartData} cx="50%" cy="50%" labelLine={false} label={({name,percent})=>`${name}: ${(percent*100).toFixed(0)}%`} outerRadius={130} dataKey="value">
                  {pieChartData.map((_,i)=><Cell key={`c${i}`} fill={COLORS[i%COLORS.length]}/>)}
                </Pie>
                <RechartsTooltip formatter={formatCurrency} contentStyle={{borderRadius:'12px',border:'none',boxShadow:'0 10px 15px -3px rgba(0,0,0,.1)'}}/>
                <Legend/>
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={filteredData} margin={{top:20,right:30,left:20,bottom:5}}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={borderColor}/>
                <XAxis dataKey="asset_type" tick={{fill:mutedColor}} axisLine={false} tickLine={false}/>
                <YAxis tickFormatter={formatCurrency} tick={{fill:mutedColor}} axisLine={false} tickLine={false}/>
                <RechartsTooltip formatter={formatCurrency} cursor={{fill:hoverBg}} contentStyle={{backgroundColor:cardBg,border:`1px solid ${borderColor}`,borderRadius:'12px',boxShadow:'0 10px 15px -3px rgba(0,0,0,.1)'}}/>
                <Legend iconType="circle"/>
                <Bar dataKey="total_purchase_cost" stackId="a" fill="#319795" name="Capital Expense (CapEx)" radius={[0,0,4,4]}/>
                <Bar dataKey="total_maintenance_cost" stackId="a" fill="#3182CE" name="Operating Expense (OpEx)" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </MotionBox>
      </AnimatePresence>
    </Box>
  );

  const renderContent = () => {
    if (loading) return <Flex justify="center" align="center" h="400px" direction="column" w="full"><Spinner size="xl" color="teal.500" thickness="4px"/><Text mt={4} color={mutedColor} fontWeight="600" letterSpacing="0.05em">Aggregating Financial Telemetry...</Text></Flex>;
    if (error) return <Alert status="error" borderRadius="xl" variant="solid" w="full"><AlertIcon/><VStack align="flex-start" spacing={1}><Text fontWeight="bold">Analytics Failure</Text><Text fontSize="sm">{error}</Text></VStack></Alert>;
    if (!costData.length) return <Alert status="info" borderRadius="xl" variant="subtle" w="full"><AlertIcon/><Text>No lifecycle cost data available.</Text></Alert>;
    if (!filteredData.length) return <Alert status="info" borderRadius="xl" variant="subtle" w="full"><AlertIcon/><Text>No asset types match your filters.</Text></Alert>;

    return (
      <VStack spacing={6} align="stretch" w="full">
        {statistics && (
          <MotionBox variants={containerVariants} initial="hidden" animate="visible" w="full">
            <SimpleGrid columns={{ base:2, md:4 }} spacing={4} w="full">
              {[
                { label:'Total TCO', value:statistics.totalCost, color:headingColor, help:'Global CapEx + OpEx' },
                { label:'Asset Procurement', value:statistics.totalPurchase, color:'teal.500', help:'Sunk Capital Expense' },
                { label:'Lifetime Service', value:statistics.totalMaintenance, color:'blue.500', help:'Ongoing Operating Cost' },
                { label:'MC/RAV Ratio', value:`${statistics.maintenanceRatio}%`, color:parseFloat(statistics.maintenanceRatio)>60?'red.500':'green.500', help:'Financial Health Index' },
              ].map((stat,idx) => (
                <MotionBox key={idx} variants={itemVariants} whileHover={{y:-4,scale:1.02}}>
                  <Stat bg={statBg} p={5} borderRadius="2xl" shadow="sm" borderWidth="1px" borderColor={borderColor}>
                    <StatLabel fontSize="xs" color={mutedColor} fontWeight="700" textTransform="uppercase" letterSpacing="0.05em">{stat.label}</StatLabel>
                    <StatNumber fontSize="2xl" fontWeight="900" color={stat.color} my={1}>{stat.value}</StatNumber>
                    <StatHelpText color={mutedColor} m={0} fontSize="10px">{stat.help}</StatHelpText>
                  </Stat>
                </MotionBox>
              ))}
            </SimpleGrid>
          </MotionBox>
        )}

        <Flex gap={4} wrap="wrap" align="center" justify="space-between" bg={bgColor} p={4} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} w="full">
          <HStack spacing={4}>
            <InputGroup maxW="280px">
              <InputLeftElement pointerEvents="none"><Icon as={MdSearch} color={mutedColor}/></InputLeftElement>
              <Input placeholder="Search classification..." value={searchInput} onChange={e=>setSearchInput(e.target.value)} size="sm" borderRadius="full" bg={cardBg} focusBorderColor="teal.400"/>
            </InputGroup>
            <Select value={sortBy} onChange={e=>setSortBy(e.target.value)} size="sm" maxW="200px" borderRadius="full" bg={cardBg} focusBorderColor="teal.400">
              <option value="total">Sort by Total TCO</option>
              <option value="purchase">Sort by CapEx</option>
              <option value="maintenance">Sort by OpEx</option>
              <option value="name">Sort by Classification</option>
            </Select>
          </HStack>
          <FormControl display="flex" alignItems="center" w="auto">
            <FormLabel mb="0" fontSize="xs" fontWeight="700" color={mutedColor} textTransform="uppercase">View</FormLabel>
            <HStack spacing={2} bg={cardBg} p={1} borderRadius="full" borderWidth="1px" borderColor={borderColor}>
              <IconButton icon={<Icon as={MdBarChart}/>} size="xs" variant={chartType==='bar'?'solid':'ghost'} colorScheme="teal" onClick={()=>setChartType('bar')} borderRadius="full" aria-label="Bar"/>
              <IconButton icon={<Icon as={MdPieChart}/>} size="xs" variant={chartType==='pie'?'solid':'ghost'} colorScheme="teal" onClick={()=>setChartType('pie')} borderRadius="full" aria-label="Pie"/>
            </HStack>
          </FormControl>
        </Flex>

        {/* --- UPDATE: FlowSense AI explainer (was a fixed "ML Insights" box) */}
        {showInsights && statistics && (
          <FlowSenseAIExplainer
            module="lifecycle_cost"
            title="Explain lifecycle economics"
            subtitle="CapEx vs OpEx by asset class and the repair-vs-replace queue"
            facts={lccExplainer.facts}
            findings={lccExplainer.findings}
            isLoading={prescriptiveLoading && !analyticsData.length}
            questions={[
              'Which asset class should we budget to replace first?',
              'Why is maintenance so high relative to purchase cost?',
              'Which prescriptions give the best saving?',
            ]}
          />
        )}
        {/* --- END UPDATE */}

        <Box bg={cardBg} p={4} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} shadow="sm" w="full">{renderChart()}</Box>

        {/* Financial Efficiency Matrix — 10/page */}
        <Box w="full">
          <HStack justify="space-between" mb={4}>
            <VStack align="start" spacing={0}>
              <Text fontWeight="800" fontSize="sm" color={mutedColor} textTransform="uppercase" letterSpacing="0.05em">Financial Efficiency Matrix</Text>
              <Text fontSize="10px" color={mutedColor}>Page {currentPage}/{totalPages} · {filteredData.length} total</Text>
            </VStack>
            <HStack spacing={2}>
              <Badge colorScheme="purple" borderRadius="full">{filteredData.length} Classifications</Badge>
              {totalPages>1&&<Badge colorScheme="teal" borderRadius="full">{ITEMS_PER_PAGE}/page</Badge>}
            </HStack>
          </HStack>
          <SimpleGrid columns={{ base:1, md:2, lg:3 }} spacing={4} w="full">
            {paginatedData.map((item, idx) => {
              const eff = getCostEfficiency(item.total_purchase_cost, item.total_maintenance_cost);
              const ratio = item.total_purchase_cost > 0 ? (item.total_maintenance_cost/item.total_purchase_cost)*100 : 0;
              const globalIdx = (currentPage-1)*ITEMS_PER_PAGE + idx;
              return (
                <MotionBox key={`${item.asset_type}-${globalIdx}`} initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{delay:idx*0.04}} whileHover={{y:-4,scale:1.02}}>
                  <Box p={5} bg={statBg} borderRadius="2xl" shadow="sm" borderWidth="1px" borderColor={borderColor} position="relative">
                    <Badge position="absolute" top={3} right={3} colorScheme="gray" fontSize="9px" borderRadius="full" px={2}>#{globalIdx+1}</Badge>
                    <HStack justify="space-between" mb={3} pr={8}>
                      <Text fontSize="sm" fontWeight="700" isTruncated maxW="70%" color={headingColor}>{item.asset_type}</Text>
                      <Badge colorScheme={eff.color} borderRadius="full" px={2} py={0.5} fontSize="9px">{eff.rating}</Badge>
                    </HStack>
                    <Progress value={Math.min(ratio,100)} colorScheme={eff.color} size="sm" borderRadius="full" hasStripe bg={progressBg}/>
                    <SimpleGrid columns={2} spacing={2} mt={3}>
                      <VStack align="start" spacing={0}><Text fontSize="9px" color={mutedColor} fontWeight="700" textTransform="uppercase">MC/RAV</Text><Text fontSize="xs" color={textColor} fontWeight="800">{ratio.toFixed(1)}%</Text></VStack>
                      <VStack align="end" spacing={0}><Text fontSize="9px" color={mutedColor} fontWeight="700" textTransform="uppercase">Total TCO</Text><Text fontSize="xs" color={textColor} fontWeight="800">{formatCurrency(item.total_cost)}</Text></VStack>
                      <VStack align="start" spacing={0}><Text fontSize="9px" color={mutedColor} fontWeight="700" textTransform="uppercase">CapEx</Text><Text fontSize="10px" color="teal.500" fontWeight="700">{formatCurrency(item.total_purchase_cost)}</Text></VStack>
                      <VStack align="end" spacing={0}><Text fontSize="9px" color={mutedColor} fontWeight="700" textTransform="uppercase">OpEx</Text><Text fontSize="10px" color="blue.500" fontWeight="700">{formatCurrency(item.total_maintenance_cost)}</Text></VStack>
                    </SimpleGrid>
                  </Box>
                </MotionBox>
              );
            })}
          </SimpleGrid>
          <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} totalItems={filteredData.length} itemsPerPage={ITEMS_PER_PAGE} borderColor={borderColor} mutedColor={mutedColor}/>
        </Box>
      </VStack>
    );
  };

  return (
    <>
      <MotionBox initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.5,ease:'easeOut'}} w="full">
        <Box bg={cardBg} borderRadius="3xl" boxShadow="2xl" p={0} borderWidth="1px" borderColor={borderColor} position="relative" overflow="hidden" w="full">
          <Box position="absolute" top="0" left="0" right="0" height="4px" bgGradient={headerGradient}/>

          <Box p={6} borderBottomWidth="1px" borderColor={borderColor} bg={cardHeaderBg} w="full">
            <Flex justify="space-between" align="center" wrap="wrap" gap={4} w="full">
              <HStack spacing={4}>
                <MotionBox initial={{scale:0,rotate:-180}} animate={{scale:1,rotate:0}} transition={{duration:0.6,type:'spring'}}>
                  <Box p={3} bgGradient={headerGradient} borderRadius="2xl" shadow="md"><Icon as={MdAttachMoney} boxSize={6} color="white"/></Box>
                </MotionBox>
                <VStack align="flex-start" spacing={0}>
                  <Heading size="md" color={headingColor} letterSpacing="-0.02em">Enterprise Lifecycle Economics</Heading>
                  <HStack spacing={2} mt={1}>
                    <Badge colorScheme="teal" fontSize="9px" borderRadius="full" px={2} py={0.5}>LCCA ENGINE</Badge>
                    {DEBUG_CONFIG.enabled&&<Badge colorScheme="gray" fontSize="9px" borderRadius="full" px={2} py={0.5}>R#{renderCountRef.current}</Badge>}
                    {lastRefresh&&<Tooltip label={`Synced: ${lastRefresh.toLocaleString()}`} hasArrow><Text fontSize="xs" color={mutedColor} cursor="help">{Math.floor((new Date()-lastRefresh)/60000)} min ago</Text></Tooltip>}
                  </HStack>
                </VStack>
              </HStack>
              <HStack spacing={2}>
                {DEBUG_CONFIG.enabled&&<Tooltip label="Debug Panel" hasArrow><IconButton icon={<Icon as={MdBugReport}/>} size="sm" variant="ghost" colorScheme="yellow" onClick={onDebugOpen} borderRadius="full"/></Tooltip>}
                <Button leftIcon={<Icon as={MdRefresh}/>} size="sm" colorScheme="teal" variant="ghost" isLoading={isRefreshing} loadingText="Syncing..." onClick={()=>{fetchCostData(true);fetchPrescriptiveData(true);}} borderRadius="full">Sync Telemetry</Button>
                <Tooltip label="Export CSV" hasArrow><IconButton icon={<Icon as={MdDownload}/>} size="sm" variant="outline" colorScheme="teal" onClick={handleExport} isDisabled={!costData.length} borderRadius="full"/></Tooltip>
              </HStack>
            </Flex>
          </Box>

          <Box p={6} w="full">
            {renderContent()}
            <Divider my={8} borderColor={borderColor}/>

            <Tabs index={mainTabIndex} onChange={setMainTabIndex} variant="soft-rounded" colorScheme="teal" isLazy w="full">
              <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={4} w="full">
                <TabList bg={bgColor} p={1.5} borderRadius="2xl" borderWidth="1px" borderColor={borderColor}>
                  <Tab borderRadius="xl" fontSize="sm" fontWeight="700" px={5} _selected={{bgGradient:headerGradient,color:'white',shadow:'md'}} transition="all 0.2s">
                    <HStack spacing={2}><Icon as={MdCalculate} boxSize={4}/><Text>Prescriptive Queue</Text>{analyticsData.length>0&&<Badge colorScheme="whiteAlpha" bg={mainTabIndex===0?'whiteAlpha.300':'teal.100'} color={mainTabIndex===0?'white':'teal.800'} borderRadius="full" px={2}>{analyticsData.length}</Badge>}</HStack>
                  </Tab>
                  <Tab borderRadius="xl" fontSize="sm" fontWeight="700" px={5} _selected={{bgGradient:headerGradient,color:'white',shadow:'md'}} transition="all 0.2s">
                    <HStack spacing={2}><Icon as={MdHistory} boxSize={4}/><Text>Ledger & History</Text>{historyData.length>0&&<Badge colorScheme="whiteAlpha" bg={mainTabIndex===1?'whiteAlpha.300':'teal.100'} color={mainTabIndex===1?'white':'teal.800'} borderRadius="full" px={2}>{historyData.length}</Badge>}</HStack>
                  </Tab>
                </TabList>
                {mainTabIndex===1&&<Button leftIcon={<Icon as={MdRefresh}/>} size="sm" variant="outline" colorScheme="teal" onClick={fetchHistoryData} isLoading={historyLoading} borderRadius="full">Fetch Ledger</Button>}
              </Flex>

              <TabPanels w="full">
                {/* Prescriptive — PERF FIX 9: paginated 12/page */}
                <TabPanel p={0} w="full">
                  <VStack align="stretch" spacing={5} w="full">
                    <HStack justify="space-between">
                      <HStack><Box p={2} bg={insightsBg} borderRadius="lg"><Icon as={MdInsights} boxSize={5} color="blue.500"/></Box><Text fontWeight="800" fontSize="sm" color={mutedColor} textTransform="uppercase" letterSpacing="0.05em">ACTIVE PRESCRIPTIONS</Text></HStack>
                      {analyticsData.length>0&&<HStack spacing={2}>
                        {analyticsData.length>100&&<Tooltip label="Backend returning large result set — consider server-side filtering" hasArrow><Badge colorScheme="orange" borderRadius="full" px={2} py={1} fontSize="xs" cursor="help">⚠️ {analyticsData.length} items</Badge></Tooltip>}
                        <Text fontSize="xs" color={mutedColor}>Page {prescriptiveCurrentPage}/{prescriptiveTotalPages}</Text>
                      </HStack>}
                    </HStack>

                    {prescriptiveLoading&&<Center py={8} w="full"><VStack spacing={3}><Spinner size="md" color="teal.400"/><Text fontSize="sm" color={mutedColor}>ML Engine computing...</Text></VStack></Center>}

                    {/* --- UPDATE: a failed call is not "all sound" */}
                    {!prescriptiveLoading&&prescriptiveError&&(
                      <Alert status="error" borderRadius="xl" w="full"><AlertIcon/><Box flex={1}><Text fontWeight="bold" fontSize="sm">Prescriptive engine unavailable</Text><Text fontSize="xs">{prescriptiveError}</Text></Box><Button size="xs" onClick={()=>fetchPrescriptiveData(true)}>Retry</Button></Alert>
                    )}
                    {!prescriptiveLoading&&!prescriptiveError&&!analyticsData.length&&!loading ? (
                      <MotionBox initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}} w="full">
                        <Center p={12} border="2px dashed" borderColor={borderColor} borderRadius="2xl" bg={bgColor} w="full">
                          <VStack spacing={4}><Circle size="64px" bg="green.50"><Icon as={MdCheckCircle} boxSize={8} color="green.500"/></Circle><VStack spacing={1}><Text fontWeight="800" fontSize="lg" color={headingColor}>No open prescriptions</Text><Text color={mutedColor} fontSize="sm" textAlign="center" maxW="400px">The prescriptive engine returned no repair-or-replace recommendations for this period.</Text></VStack></VStack>
                        </Center>
                      </MotionBox>
                    ) : !prescriptiveLoading&&!prescriptiveError&&(
                      <MotionBox variants={containerVariants} initial="hidden" animate="visible" w="full">
                        <SimpleGrid columns={{ base:1, xl:2 }} spacing={6} w="full">
                          <AnimatePresence>
                            {paginatedPrescriptive.map(item=>(
                              <MotionBox key={itemId(item)} variants={itemVariants} exit="exit" w="full">
                                {/* --- UPDATE: missing values stay null (card shows "—"), either id key */}
                                <PrescriptiveActionCard
                                  prescription={{ assetId: itemId(item), assetName: item.assetName||item.asset_name||`Asset ${itemId(item)}`, action: item.action||'Unknown', reason: item.reason||'Analysis pending...', confidence: finiteOrNull(item.confidence ?? item.confidence_score), analysis: { annualized_cost_of_new_asset: finiteOrNull(item.analysis?.annualized_cost_of_new_asset), annualized_cost_of_repair: finiteOrNull(item.analysis?.annualized_cost_of_repair), remaining_lifespan: item.analysis?.remaining_lifespan ?? 'N/A', failure_probability: finiteOrNull(item.analysis?.failure_probability), efficiency_rating: finiteOrNull(item.analysis?.efficiency_rating), additional_metrics: true } }}
                                  isLoading={loading} debugLogger={logger} onApplied={handleActionApplied}
                                />
                              </MotionBox>
                            ))}
                          </AnimatePresence>
                        </SimpleGrid>
                        <PaginationControls currentPage={prescriptiveCurrentPage} totalPages={prescriptiveTotalPages} onPageChange={handlePrescriptivePageChange} totalItems={analyticsData.length} itemsPerPage={PRESCRIPTIVE_PAGE_SIZE} borderColor={borderColor} mutedColor={mutedColor}/>
                      </MotionBox>
                    )}
                  </VStack>
                </TabPanel>

                <TabPanel p={0} w="full">
                  <PrescriptiveHistoryTab history={historyData} isLoading={historyLoading} onRefresh={fetchHistoryData} searchTerm={historySearchTerm} onSearchChange={setHistorySearchTerm}/>
                </TabPanel>
              </TabPanels>
            </Tabs>

            {/* Diagnostics */}
            <Box mt={8} w="full">
              <Button size="xs" variant="ghost" rightIcon={isAdvancedOpen?<ChevronUpIcon/>:<ChevronDownIcon/>} onClick={onToggleAdvanced} borderRadius="full" color={mutedColor}>System Payload Diagnostics</Button>
              <Collapse in={isAdvancedOpen} animateOpacity>
                <Box mt={3} p={5} bg={bgColor} borderRadius="2xl" borderWidth="1px" borderColor={borderColor} fontSize="sm" w="full">
                  <SimpleGrid columns={{ base:1, md:3 }} spacing={6} w="full">
                    <Box>
                      <Text fontWeight="800" mb={3} color={mutedColor} textTransform="uppercase" letterSpacing="0.05em">Dataset</Text>
                      <VStack align="flex-start" spacing={2} w="full">
                        <Code p={2} fontSize="xs" borderRadius="md" w="full" bg={statBg} color={textColor}>Cost Nodes: {costData.length}</Code>
                        <Code p={2} fontSize="xs" borderRadius="md" w="full" bg={statBg} color={textColor}>Filtered: {filteredData.length} · Pg {currentPage}/{totalPages}</Code>
                        <Code p={2} fontSize="xs" borderRadius="md" w="full" bg={statBg} color={textColor}>Prescriptive: {analyticsData.length} · Pg {prescriptiveCurrentPage}/{prescriptiveTotalPages}</Code>
                        <Code p={2} fontSize="xs" borderRadius="md" w="full" bg={statBg} color={textColor}>Chart: {chartType} · Renders: {renderCountRef.current}</Code>
                        <Code p={2} fontSize="xs" borderRadius="md" w="full" bg={statBg} color={textColor}>Last Sync: {lastRefresh?.getTime()||'NULL'}</Code>
                      </VStack>
                    </Box>
                    <Box>
                      <Text fontWeight="800" mb={3} color={mutedColor} textTransform="uppercase" letterSpacing="0.05em">MC/RAV Distribution</Text>
                      <VStack align="flex-start" spacing={2} w="full">
                        {['Excellent','Good','Fair','Poor'].map(rating=>{
                          const count = costData.filter(d=>getCostEfficiency(d.total_purchase_cost,d.total_maintenance_cost).rating===rating).length;
                          return <HStack key={rating} w="full" justify="space-between" p={2} bg={statBg} borderRadius="md"><Badge colorScheme={rating==='Excellent'?'teal':rating==='Good'?'blue':rating==='Fair'?'orange':'red'} borderRadius="full" px={2}>{rating}</Badge><Text fontSize="xs" fontWeight="bold" color={textColor}>{count} Nodes</Text></HStack>;
                        })}
                      </VStack>
                    </Box>
                    {/* PERF FIX 10: memoised snapshot */}
                    <Box>
                      <Text fontWeight="800" mb={3} color={mutedColor} textTransform="uppercase" letterSpacing="0.05em">Cache</Text>
                      <VStack align="flex-start" spacing={2} w="full">
                        {diagnosticsCacheSnapshot.entries.length>0 ? diagnosticsCacheSnapshot.entries.map(e=>(
                          <HStack key={e.key} w="full" justify="space-between" p={2} bg={statBg} borderRadius="md">
                            <Text fontSize="9px" fontFamily="mono" isTruncated maxW="55%" color={textColor}>{e.key.split(':')[0]}</Text>
                            <HStack spacing={1}><Badge colorScheme={e.expired?'red':'green'} fontSize="8px" borderRadius="full">{e.expired?'STALE':`${(e.ageMs/1000).toFixed(0)}s`}</Badge><Badge colorScheme="blue" fontSize="8px" borderRadius="full">{e.hitCount}h</Badge></HStack>
                          </HStack>
                        )) : <Code p={2} fontSize="xs" borderRadius="md" w="full" bg={statBg} color={textColor}>Cache empty</Code>}
                        <Button size="xs" colorScheme="red" variant="outline" borderRadius="full" onClick={()=>{cacheInvalidate();logger.cache('🗑️ Manual flush');toast({title:'Cache Flushed',status:'info',duration:2000,variant:'subtle'});}}>Flush Cache</Button>
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </Box>
      </MotionBox>
      <DebugPanel logger={logger} isOpen={isDebugOpen} onClose={onDebugClose}/>
    </>
  );
};

export default LifecycleCostWidget;